const INTERCOM_APP_ID = 'jyc9kifw'

export interface IntercomUser {
  user_id: string | number
  email?: string
  name?: string
  created_at?: number
  [key: string]: any
}

const isIntercomReady = (): boolean => {
  // @ts-ignore
  return typeof window.Intercom === 'function'
}

const waitForIntercom = (maxWaitMs: number = 5000): Promise<boolean> => {
  return new Promise((resolve) => {
    if (isIntercomReady()) {
      resolve(true)
      return
    }

    let attempts = 0
    const maxAttempts = maxWaitMs / 100
    const interval = setInterval(() => {
      attempts++
      if (isIntercomReady()) {
        clearInterval(interval)
        resolve(true)
      } else if (attempts >= maxAttempts) {
        clearInterval(interval)
        resolve(false)
      }
    }, 100)
  })
}

// Used by bootIntercomAsUser: try booting immediately, fall back to the
// queue-system call, then wait for the script to load if neither is ready yet.
const bootWithConfig = async (bootConfig: Record<string, any>): Promise<void> => {
  const boot = () => {
    // @ts-ignore
    if (isIntercomReady()) {
      // @ts-ignore
      window.Intercom('boot', bootConfig)
      return true
    }
    return false
  }

  if (boot()) {
    return
  }

  // @ts-ignore
  if (window.Intercom) {
    // @ts-ignore
    window.Intercom('boot', bootConfig)
    return
  }

  const ready = await waitForIntercom()
  if (ready) {
    boot()
  }
}

// Used by bootIntercomAsUser to build the common config shape; user_id is excluded from the
// generic spread here and added back explicitly by the caller.
const buildBootConfig = (identity: Partial<IntercomUser>): Record<string, any> => ({
  app_id: INTERCOM_APP_ID,
  email: identity.email,
  name: identity.name,
  created_at: identity.created_at ?? Math.floor(Date.now() / 1000),
  hide_default_launcher: false,
  ...Object.keys(identity)
    .filter(key => !['user_id', 'email', 'name', 'created_at'].includes(key))
    .reduce((acc, key) => ({ ...acc, [key]: (identity as IntercomUser)[key] }), {})
})

export const bootIntercomAsUser = async (user: IntercomUser): Promise<void> => {
  await bootWithConfig({ ...buildBootConfig(user), user_id: user.user_id })
}

// carrier-api hardcodes 3 starting attempts (CarrierOnboardingService#createCarrierEnrollmentInvitationState,
// CarrierEnrollmentService#resetPendingAttempts*) — not exposed as a "max attempts" field in any response.
const MAX_CREDENTIAL_VERIFICATION_ATTEMPTS = 3

// invitationInfo.pendingAttempts (CarrierEnrollmentInvitationState.pendingAttempts) starts at 3 and
// decrements per failed credential verification; carrier-api auto-resets it to 3 on the next invitation
// GET once it hits 0. It's already present in the getCobList/onboardCarrier response — no new call needed.
export const credentialFailureCountFromPendingAttempts = (pendingAttempts: unknown): number | undefined =>
  typeof pendingAttempts === 'number'
    ? MAX_CREDENTIAL_VERIFICATION_ATTEMPTS - pendingAttempts
    : undefined

export const bootIntercomAsVisitor = async (): Promise<void> => {
  const bootConfig = {
    app_id: INTERCOM_APP_ID,
    hide_default_launcher: false
  }

  const boot = () => {
    // @ts-ignore
    if (isIntercomReady()) {
      // @ts-ignore
      window.Intercom('boot', bootConfig)
      return true
    }
    return false
  }

  if (boot()) {
    return
  }

  // @ts-ignore
  if (window.Intercom) {
    // @ts-ignore
    window.Intercom('boot', bootConfig)
    return
  }

  const ready = await waitForIntercom()
  if (ready) {
    boot()
  }
}

export const shutdownIntercom = (): void => {
  // @ts-ignore
  if (isIntercomReady()) {
    // @ts-ignore
    window.Intercom('shutdown')
  }
}

export const showIntercom = (): void => {
  // @ts-ignore
  if (isIntercomReady()) {
    // @ts-ignore
    window.Intercom('show')
  }
}

export const showIntercomLauncher = (): void => {
  // @ts-ignore
  if (isIntercomReady()) {
    // @ts-ignore
    window.Intercom('update', {
      hide_default_launcher: false
    })
  }
}

export const hideIntercomLauncher = (): void => {
  // @ts-ignore
  if (isIntercomReady()) {
    // @ts-ignore
    window.Intercom('update', {
      hide_default_launcher: true
    })
  }
}
